import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  FileText,
  Users,
  Calendar,
  BookOpen,
  Settings,
  Star,
  MessageSquareQuote,
  Search,
  Globe,
  CreditCard,
  TrendingUp,
  Send,
  Upload,
  FileCheck,
  Bell,
  Clock,
  Mail,
  ChevronDown,
  ChevronRight,
  User,
  Shield,
  Menu,
  X,
  RefreshCw,
  GraduationCap
} from 'lucide-react';

const DashboardSidebar = ({ userRole = 'user', activeSection, onSectionChange }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  // Close sidebar on mobile when navigating
  useEffect(() => {
    setIsMobileOpen(false);
  }, [activeSection]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Define menu structure based on role
  const getMenuItems = () => {
    if (userRole === 'admin' || userRole === 'super_admin') {
      const baseItems = [
        {
          section: 'CONTENT MANAGEMENT',
          items: [
            { id: 'agencies', label: 'Agencies', icon: Building2, tab: 'agencies' },
            { id: 'jobs', label: 'Job Posts', icon: Briefcase, tab: 'jobs' },
            { id: 'blogs', label: 'Blog Posts', icon: BookOpen, tab: 'blog' },
            { id: 'testimonials', label: 'Testimonials', icon: MessageSquareQuote, tab: 'testimonials' },
            { id: 'course-inquiries', label: 'Course Inquiries', icon: Mail, tab: 'course-inquiries' },
          ]
        },
        {
          section: 'USER MANAGEMENT',
          items: [
            { id: 'multi-apply', label: 'Multi-Apply Orders', icon: Send, tab: 'multi-apply' },
            { id: 'upsell-stats', label: 'Pop-Up Stats', icon: TrendingUp, tab: 'upsell-stats' },
            { id: 'job-applier', label: 'Job Applier', icon: Briefcase, tab: 'job-applier' },
            { id: 'ucp', label: 'Candidate Profiles', icon: User, tab: 'ucp' },
            { id: 'user-cvs', label: 'User CVs', icon: FileText, tab: 'user-cvs' },
            { id: 'cv-reviews', label: 'CV Reviews', icon: FileCheck, tab: 'cv-reviews' },
            { id: 'submissions', label: 'CV Submissions', icon: FileText, tab: 'submissions' },
            { id: 'consultations', label: 'Consultations', icon: Calendar, tab: 'consultations' },
            { id: 'paid-subscribers', label: 'Paid Subscribers', icon: CreditCard, tab: 'paid-subscribers' },
            { id: 'notifications', label: 'Notifications', icon: Bell, tab: 'notifications' },
          ]
        },
        {
          section: 'TOOLS',
          items: [
            { id: 'scraper', label: 'Job Scraper', icon: Search, tab: 'scraper' },
            { id: 'job-sources', label: 'Auto-Sync', icon: RefreshCw, tab: 'job-sources' },
            { id: 'practical-courses-admin', label: 'Practical Courses', icon: GraduationCap, link: '/admin/practical-courses' },
            { id: 'agency-scraper', label: 'Agency Scraper', icon: Globe, tab: 'agency-scraper' },
          ]
        },
      ];
      
      // Add Super Admin section only for super_admin role
      if (userRole === 'super_admin') {
        baseItems.push({
          section: 'SUPER ADMIN',
          items: [
            { id: 'admin-management', label: 'Admin Management', icon: Shield, tab: 'admin-management' },
            { id: 'staff-management', label: 'Staff Accounts', icon: Users, tab: 'staff-management' },
            { id: 'site-settings', label: 'Site Settings', icon: Settings, tab: 'site-settings' },
          ]
        });
      }
      
      return baseItems;
    }
    
    // Regular user menu
    return [
      {
        section: 'DASHBOARD',
        items: [
          { id: 'find-recruiters', label: 'Find Recruiters', icon: Search, tab: 'find-recruiters' },
          { id: 'multi-apply', label: 'Multi-Apply', icon: Send, tab: 'multi-apply' },
          { id: 'agency-jobs', label: 'Agency Jobs', icon: Briefcase, tab: 'agency-jobs' },
          { id: 'cv-review', label: 'CV Review', icon: FileCheck, tab: 'cv-review' },
          { id: 'consultations', label: 'Consultations', icon: Calendar, tab: 'consultations' },
          { id: 'notifications', label: 'Notifications', icon: Bell, tab: 'notifications' },
          { id: 'settings', label: 'Settings', icon: Settings, tab: 'settings' },
        ]
      },
    ];
  };

  const menuItems = getMenuItems();

  const handleItemClick = (item) => {
    if (item.link) {
      // Navigation handled by Link component
      setIsMobileOpen(false);
      return;
    }
    if (item.tab && onSectionChange) {
      onSectionChange(item.tab);
      setIsMobileOpen(false);
    }
  };

  const isActive = (item) => {
    if (item.link) {
      return location.pathname === item.link;
    }
    return activeSection === item.tab;
  };

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button
        className="lg:hidden fixed top-20 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-neutral-200"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isMobileOpen ? (
          <X className="w-6 h-6 text-neutral-700" />
        ) : (
          <Menu className="w-6 h-6 text-neutral-700" />
        )}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "bg-white border-r border-neutral-200 min-h-screen flex-shrink-0 flex flex-col relative z-50 transition-transform duration-300",
        "w-64",
        // Mobile: fixed position, slide in/out
        "fixed lg:relative",
        "top-0 left-0",
        isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="p-4 flex-1 overflow-y-auto pb-20">
          {/* Logo/Brand */}
          <div className="flex items-center gap-2 mb-6 px-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-neutral-900">
              {userRole === 'admin' || userRole === 'super_admin' ? 'Admin Panel' : 'Dashboard'}
            </span>
          </div>

          {/* Navigation */}
          <nav className="space-y-6">
            {menuItems.map((group) => (
              <div key={group.section}>
                <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider px-2 mb-2">
                  {group.section}
                </h3>
                <ul className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);
                    
                    const content = (
                      <div
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200',
                          active
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                        )}
                        onClick={() => handleItemClick(item)}
                        data-testid={`sidebar-item-${item.id}`}
                      >
                        <Icon className={cn(
                          'w-5 h-5 flex-shrink-0',
                          active ? 'text-primary-600' : 'text-neutral-400'
                        )} />
                        <span className="text-sm">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    );

                    if (item.link) {
                      return (
                        <li key={item.id}>
                          <Link to={item.link} onClick={() => setIsMobileOpen(false)}>
                            {content}
                          </Link>
                        </li>
                      );
                    }

                    return <li key={item.id}>{content}</li>;
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* User Profile at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-200 bg-white w-64">
          <Link 
            to="/settings" 
            className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 transition-colors"
            onClick={() => setIsMobileOpen(false)}
          >
            <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-neutral-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">Profile</p>
              <p className="text-xs text-neutral-500">View settings</p>
            </div>
          </Link>
        </div>
      </aside>
    </>
  );
};

export default DashboardSidebar;
